import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Store } from 'types/store';
import { setRLRJourneyError } from 'actions/journeys/loyaltyRewardsActions';
import { ClaimRewardError } from 'types/journeys/loyaltyRewards';
import trackEvent from 'services/AnalyticsService';

interface UseRLRJourneyErrorHandlerProps {
  loading: boolean;
  isEligible: boolean;
  claimRewardError: ClaimRewardError | null;
}

const useErrorHandler = ({
  loading,
  isEligible,
  claimRewardError,
}: UseRLRJourneyErrorHandlerProps) => {
  const dispatch = useDispatch();
  const { pageModel, journeyError } = useSelector((state: Store) => state.journeys.loyaltyRewards);

  useEffect(() => {
    if (pageModel && !loading && !journeyError) {
      if (!isEligible || (claimRewardError && claimRewardError?.policyRewardEligibility !== 'PolicyEligibleForRewards')) {
        trackEvent('MyRAC_RLR_Error_Eligibility');
        dispatch(
          setRLRJourneyError({
            type: 'Eligibility error',
            title: 'Sorry, we’re having trouble with your offer',
            description:
              'Give us a call on <a href="tel:0330 159 8691">0330 159 8691</a> and we’ll make sure you don’t miss out.',
            messageType: 'ERROR',
          }),
        );
      }

      if (pageModel?.rewardClaimed) {
        trackEvent('MyRAC_RLR_Error_Redeemed');
        dispatch(
          setRLRJourneyError({
            type: 'Already redeemed error',
            title: 'Hmm, looks like you’ve already got this offer',
            description:
              'If this isn’t right, you’re looking to change your offer or you need to discuss your renewal, please call us on <a href="tel:0330 159 0369">0330 159 0369</a>',
            messageType: 'ERROR',
          }),
        );
      }

      if (pageModel?.sspBusinessWindowClosed || claimRewardError?.sspBusinessWindowClosedWhenClaimAttempted) {
        trackEvent('MyRAC_RLR_Error_Closure');
        dispatch(
          setRLRJourneyError({
            type: 'Business closure error',
            title: 'You just missed us!',
            description:
              'We’ve gone offline for the night. But don’t worry, we’ll be live again at 6am. See you then!',
            messageType: 'CLOSED',
          }),
        );
      }
    } else if (!journeyError && !loading) {
      trackEvent('MyRAC_RLR_Error_Technical');
      dispatch(
        setRLRJourneyError({
          type: 'Technical error',
          title: 'Sorry, we’re having trouble with your offer',
          description:
            'We’re experiencing some technical difficulties. Try refreshing the page or come back later.<br /> Prefer to talk? Call us on <a href="tel:0330 159 8691">0330 159 8691</a>',
          messageType: 'ERROR',
        }),
      );
    }
  }, [pageModel, loading, journeyError, isEligible, claimRewardError, dispatch]);
};

export default useErrorHandler;
